import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';
import { AnalyticsResponse, SystemStatus, PromptsResponse } from '../types/api';

interface SystemContextType {
  analytics: AnalyticsResponse | null;
  systemStatus: SystemStatus | null;
  prompts: PromptsResponse | null;
  healthStatus: any;
  notionStatus: any;
  isLoading: boolean;
  lastUpdated: Date | null;
  refreshData: () => Promise<void>;
  refreshPrompts: () => Promise<void>;
  forceRefreshData: () => Promise<void>;
  forceRefreshPrompts: () => Promise<void>;
}

const SystemContext = createContext<SystemContextType | undefined>(undefined);

export const useSystemData = () => {
  const context = useContext(SystemContext);
  if (context === undefined) {
    throw new Error('useSystemData must be used within a SystemProvider');
  }
  return context;
};

interface SystemProviderProps {
  children: ReactNode;
}

export const SystemProvider: React.FC<SystemProviderProps> = ({ children }) => {
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [prompts, setPrompts] = useState<PromptsResponse | null>(null);
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [notionStatus, setNotionStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchPrompts = async () => {
    try {
      const promptsData = await apiClient.getPrompts(1, 100);
      setPrompts(promptsData);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [analyticsData, statusData, healthData] = await Promise.all([
        apiClient.getAnalytics(),
        apiClient.getSystemStatus(),
        apiClient.getHealth()
      ]);
      
      setAnalytics(analyticsData);
      setSystemStatus(statusData);
      setHealthStatus(healthData);
      
      // Get Notion status from system status (avoid duplicate calls)
      if (statusData?.notion_connection) {
        setNotionStatus({
          status: statusData.notion_connection.status,
          prompt_count: statusData.notion_connection.prompt_count,
          message: "Connection status from system data"
        });
      }
      
      setLastUpdated(new Date());

      // Fetch prompts data separately (uses cache)
      await fetchPrompts();
    } catch (error) {
      console.error('Error fetching system data:', error);
      // If there's an auth error, the API client interceptor will handle redirect to login
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    // Auto-refresh: Fetches data from cache (backend refreshes cache automatically every 5 minutes)
    await fetchData();
  };

  const forceRefreshData = async () => {
    // Manual refresh: Force cache refresh first, then fetch all data
    try {
      await apiClient.refreshNotionCache();
    } catch (error) {
      console.error('Error refreshing cache:', error);
    }
    await fetchData();
  };

  const refreshPrompts = async () => {
    // Just fetch prompts from cache
    await fetchPrompts();
  };

  const forceRefreshPrompts = async () => {
    // Force refresh prompts cache and fetch prompts (for manual refresh or after CRUD operations)
    try {
      await apiClient.refreshNotionCache();
      await fetchPrompts();
    } catch (error) {
      console.error('Error refreshing prompts:', error);
    }
  };

  // Global auto-refresh timer (2 minutes)
  useEffect(() => {
    fetchData(); // Initial load
    const globalRefreshInterval = setInterval(() => {
      fetchData(); 
    }, 120000); // 2 minutes

    return () => clearInterval(globalRefreshInterval);
  }, []); 

  const value: SystemContextType = {
    analytics,
    systemStatus,
    prompts,
    healthStatus,
    notionStatus,
    isLoading,
    lastUpdated,
    refreshData,
    refreshPrompts,
    forceRefreshData,
    forceRefreshPrompts,
  };

  return (
    <SystemContext.Provider value={value}>
      {children}
    </SystemContext.Provider>
  );
}; 